
import { computed, ref, watch, defineComponent } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useNSProtectedStore } from '@/store';
import ProgressBar from '@/components/utils/ux/ProgressBar.vue';
import STSLandingBody from '@/components/sts/stsLanding/STSLandingBody.vue';
import STSLandingGenericError from '@/components/sts/stsLanding/STSLandingGenericError.vue';
import {
  CANCEL_HANDLE_PROGRESS_BAR,
  HANDLE_PROGRESS_BAR,
  HANDLE_PROGRESS_BAR_FINISH_SEGMENTS,
} from '@/store/sagas/patterns';
import * as tkGCAP from '@/store/modules/getC360AcctsParallelModule/treekeys';
import * as tkGRPP from '@/store/modules/getRXProductInfoParallelModule/treekeys';
import * as tkPB from '@/store/modules/progressbar/treekeys';
import * as tkSTSL from '@/store/modules/stsLandingModule/treekeys';
import {
  ROUTERNAME_STSIFRAME,
  ROUTERNAME_STSSELECTACCT,
  REGEXP_EMAIL,
  ROUTERNAME_STSPOSTLOGIN,
} from '@/common/strings';
import { GetC360AcctsState } from '@/store/modules/getC360AcctsParallelModule/types';
import { useNSProtectedSagaDispatch } from '@/store/protectedVuexSaga';
import { GetRXProductInfoState } from '@/store/modules/getRXProductInfoParallelModule/types';
import { useGtmUtilsSingleton } from '@/common/utils/gtmUtilsSingleton';
import {
  VUE_APP_BRIGHTSPEED_DAYONEPLUS_TOGGLE,
  VUE_APP_BRIGHTSPEED_DSSR_URL,
} from '@/common/envVars';
import { useVuelidateRulesSingleton } from '@/common/utils/vuelidateRulesSingleton';
import { useDataUtilsSingleton } from '@/common/utils/dataUtilsSingleton';

export default defineComponent({
  name: 'STSLanding',
  components: {
    STSLandingGenericError,
    ProgressBar,
    STSLandingBody,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useNSProtectedStore(tkSTSL.STSLANDINGMODULE);
    const sagaDispatch = useNSProtectedSagaDispatch(tkSTSL.STSLANDINGMODULE);
    const acctNumQuery = ref('');
    const acctNumQuerySubmit = ref('');
    const billingZip = ref('');
    const billingZipSubmit = ref('');
    const getC360AcctsState = ref<GetC360AcctsState>();
    const getRXProductInfoState = ref<GetRXProductInfoState>();
    const isValid = ref(false);
    const loadingQueryParams = ref(false);
    const gtm = useGtmUtilsSingleton();
    const isBrightSpeed = ref(false);
    const vuelidateRulesSingleton = useVuelidateRulesSingleton();
    const dataUtilsSingleton = useDataUtilsSingleton();
    const urlQuery = ref('');

    if (route.query?.ssoEncInfo) {
      router.push({ name: ROUTERNAME_STSPOSTLOGIN });
    } else {
      gtm.trackGTMEvent({
        page: {
          pageType: 'sts',
          pageName: 'ctl|rsd|support|repair|auth|enter_credentials',
        },
      });
    }

    const updateValidation = (valid: boolean) => {
      isValid.value = valid;
    };

    const getFieldName = (acctNum: string) => {
      if (acctNum && acctNum?.length === 10) {
        return 'accountPhone';
      }
      return 'accountId';
    };

    const handleResp = (acctNum: string, zip: string) => {
      if (!isValid.value) {
        return;
      }

      const numActiveAccts =
        store.getters[
          tkGCAP
            .NS_GET_GETC360ACCTSPARALLELMODULE_NUMKEYNONINACTIVEANDNONSUSPACCTSMAP
        ]?.[acctNum].length;

      if (numActiveAccts === 1) {
        gtm.trackGTMEvent({
          page: {
            pageType: 'sts',
            pageName: 'sts',
          },
          customer: {
            accountType: gtm.pii(
              dataUtilsSingleton.getCustomerType(
                store.getters[
                  tkGCAP
                    .NS_GET_GETC360ACCTSPARALLELMODULE_NUMKEYNONINACTIVEANDNONSUSPACCTSMAP
                ]?.[acctNum]?.[0]?.customerType
              )
            ),
            [vuelidateRulesSingleton.isAcctOrPpb(acctNum)
              ? getFieldName(acctNum)
              : 'accountEmail']: gtm.pii(acctNum),
          },
        });
      }

      const companyOwnerIDforZip =
        store.getters[
          tkGCAP
            .NS_GET_GETC360ACCTSPARALLELMODULE_NUMKEYNONINACTIVEANDNONSUSPACCTSMAP
        ]?.[acctNum]?.[0]?.companyOwnerId;

      if (numActiveAccts && numActiveAccts >= 1) {
        if (
          VUE_APP_BRIGHTSPEED_DAYONEPLUS_TOGGLE === 'true' &&
          numActiveAccts === 1 &&
          companyOwnerIDforZip === '4'
        ) {
          isBrightSpeed.value = true;
          gtm.trackGTMEvent({
            event: 'sts_bspd_exception_msg',
            page: {
              pageType: 'sts',
              pageName: 'STSLanding',
            },
          });
        } else if (REGEXP_EMAIL.test(acctNum)) {
          router.push({
            name:
              numActiveAccts > 1
                ? ROUTERNAME_STSSELECTACCT
                : ROUTERNAME_STSIFRAME,
            params: {
              accountNumber:
                numActiveAccts > 1
                  ? null
                  : store.getters[
                      tkGCAP
                        .NS_GET_GETC360ACCTSPARALLELMODULE_NUMKEYNONINACTIVEANDNONSUSPACCTSMAP
                    ]?.[acctNum]?.[0]?.accountNumber,
              email: numActiveAccts > 1 ? acctNum : null,
              zipCode: zip,
            },
          });
        } else {
          router.push({
            name:
              numActiveAccts > 1
                ? ROUTERNAME_STSSELECTACCT
                : ROUTERNAME_STSIFRAME,
            params: {
              accountNumber:
                acctNum.length === 10 || acctNum.length === 13
                  ? store.getters[
                      tkGCAP
                        .NS_GET_GETC360ACCTSPARALLELMODULE_NUMKEYNONINACTIVEANDNONSUSPACCTSMAP
                    ]?.[acctNum]?.[0]?.accountNumber
                  : acctNum,
              email: null,
              zipCode: zip,
            },
            query: {
              [REGEXP_EMAIL.test(acctNum) ? 'email' : 'accountNumber']: acctNum,
            },
          });
        }
      }
    };

    watch(
      () => store.getters[tkSTSL.NS_GET_STSLANDINGMODULE_SELECTEDACCOUNTNUMBER],
      (newVal: any) => {
        acctNumQuery.value = newVal;
      },
      { immediate: true }
    );
    watch(
      () => store.getters[tkSTSL.NS_GET_STSLANDINGMODULE_SELECTEDZIPCODE],
      (newVal: any) => {
        billingZip.value = newVal;
      },
      { immediate: true }
    );
    watch(
      () =>
        store.getters[
          tkGCAP.NS_GET_GETC360ACCTSPARALLELMODULE_GETC360ACCTSPARALLELMAP
        ]?.[acctNumQuery.value],
      (newVal: any) => {
        getC360AcctsState.value = newVal;
      },
      { immediate: true }
    );
    watch(
      () =>
        store.getters[
          tkGRPP
            .NS_GET_GETRXPRODUCTINFOPARALLELMODULE_GETRXPRODUCTINFOPARALLELMAP
        ]?.[acctNumQuery.value],
      (newVal: any) => {
        getRXProductInfoState.value = newVal;
      },
      { immediate: true }
    );

    watch(
      () => [
        store.getters[tkSTSL.NS_GET_STSLANDINGMODULE_SUBMITPENDING],
        getC360AcctsState.value,
      ],
      (newVal: any) => {
        const [submitPending, st] = newVal;
        if (st && submitPending) {
          acctNumQuerySubmit.value = acctNumQuery.value;
          billingZipSubmit.value = billingZip.value;
          sagaDispatch(HANDLE_PROGRESS_BAR, 1);
        } else if (st && !submitPending) {
          sagaDispatch(HANDLE_PROGRESS_BAR_FINISH_SEGMENTS, 1);
          if (store.getters[tkPB.NS_GET_PROGRESSBAR_VISUALFINISHED]) {
            handleResp(acctNumQuerySubmit.value, billingZipSubmit.value);
          }
        }
      }
    );

    watch(
      () => store.getters[tkPB.NS_GET_PROGRESSBAR_VISUALFINISHED],
      (newVal: any) => {
        if (newVal) {
          handleResp(acctNumQuerySubmit.value, billingZipSubmit.value);
        }
      }
    );

    onBeforeRouteLeave(() => {
      sagaDispatch(CANCEL_HANDLE_PROGRESS_BAR);
    });

    let qAcctNum = (route.params?.accountNumber ||
      route.params?.email) as string;
    let qZipCode = route.params?.zipCode as string;

    const queryAccountNumber = route.query?.accountNumber as string;
    const queryEmail = route.query?.email as string;
    const queryPhoneNumber = route.query?.phoneNumber as string;
    let acctNumQueryType = '';

    if (queryAccountNumber) {
      qAcctNum = queryAccountNumber || qAcctNum;
      acctNumQueryType = 'accountNumber';
    } else if (queryEmail) {
      qAcctNum = queryEmail || qAcctNum;
      acctNumQueryType = 'email';
    } else if (queryPhoneNumber) {
      qAcctNum = queryPhoneNumber || qAcctNum;
      acctNumQueryType = 'phoneNumber';
    }
    qZipCode = (route.query?.zipCode || qZipCode) as string;

    if (qAcctNum || qZipCode) {
      loadingQueryParams.value = true;
      // urlQuery.value = getRXProductInfoState.value?.rxProductInfoError
      //   ? ''
      //   : (qAcctNum ? `${acctNumQueryType}=${qAcctNum}&` : '') +
      //     (qZipCode ? `zipCode=${qZipCode}` : '');
      urlQuery.value =
        (qAcctNum ? `${acctNumQueryType}=${qAcctNum}&` : '') +
        (qZipCode ? `zipCode=${qZipCode}` : '');
      Promise.all([
        store.dispatch(
          tkSTSL.NS_ACTION_STSLANDINGMODULE_SELECTEDACCOUNTNUMBER,
          { selectedAccountNumber: qAcctNum }
        ),
        store.dispatch(tkSTSL.NS_ACTION_STSLANDINGMODULE_SELECTEDZIPCODE, {
          selectedZipCode: qZipCode,
        }),
        store.dispatch(
          tkSTSL.NS_ACTION_STSLANDINGMODULE_SHOWVALIDATEERRORSACCOUNTNUMBER,
          { showValidateErrorsAccountNumber: true }
        ),
      ]).then(() => {
        loadingQueryParams.value = false;
      });
    }

    return {
      submitProgressValue: computed(
        () => store.getters[tkPB.NS_GET_PROGRESSBAR_PROGRESSVALUE]
      ),
      submitProgressBarAppear: computed(
        () => store.getters[tkPB.NS_GET_PROGRESSBAR_PROGRESSBARAPPEAR]
      ),
      loadingQueryParams,
      urlQuery,
      updateValidation,
      getC360AcctsStateSubmit: computed(
        () =>
          store.getters[
            tkGCAP.NS_GET_GETC360ACCTSPARALLELMODULE_GETC360ACCTSPARALLELMAP
          ]?.[acctNumQuerySubmit.value]
      ),
      acctNumQuerySubmit,
      getRXProductInfoState,
      isBrightSpeed,
      goToBrightSpeed: computed(() => `${VUE_APP_BRIGHTSPEED_DSSR_URL}/sts`),
    };
  },
});
